import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import PropTypes from 'prop-types';

const MenuModal = ({ isOpen, onClose, onSave }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const validateName = (value) => {
        if (!value || value.trim() === '') {
            return t('validation.nameRequired');
        }
        if (value.trim().length < 2) {
            return t('validation.nameMinLength') || 'Naam moet minimaal 2 karakters bevatten';
        }
        if (value.trim().length > 100) {
            return t('validation.nameMaxLength') || 'Naam mag maximaal 100 karakters bevatten';
        }
        return '';
    };

    const handleChange = useCallback((e) => {
        const { value } = e.target;
        setName(value);
        // Reset error on typing
        if (nameError) {
            setNameError('');
        }
    }, [nameError]);

    const handleSave = async () => {
        const trimmedName = name.trim();
        const error = validateName(trimmedName);

        if (error) {
            setNameError(error);
            return;
        }

        try {
            setIsSaving(true);
            await onSave({
                id: 0,
                name: trimmedName
            });
            
            // Reset form after successful save
            setName('');
            setNameError('');
            onClose();
        } catch (error) {
            console.error('Error saving menu:', error);
            setNameError(t('messages.errorSavingMenu') || 'Fout bij het opslaan van het menu');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (!isSaving) {
            setName('');
            setNameError('');
            onClose();
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !nameError && name.trim()) {
            handleSave();
        } else if (e.key === 'Escape') {
            handleClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-md relative animate-in fade-in duration-200">
                <button
                    onClick={handleClose}
                    disabled={isSaving}
                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
                    aria-label={t('common.close')}
                >
                    <X size={24} />
                </button>

                <div className="p-8">
                    <div className="mb-6">
                        <h2 className="text-xl font-inter text-ps-blue-dark mb-2">
                            {t('dashboard.addMenu')}
                        </h2>
                        <p className="text-sm text-gray-600 font-inter">
                            {t('dashboard.addMenuDescription') || 'Voer een naam in voor je nieuwe menukaart'}
                        </p>
                    </div>

                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('common.name')} <span className="text-red-500">*</span>
                            </label>
                            <Input
                                type="text"
                                name="name"
                                value={name}
                                onChange={handleChange}
                                onKeyDown={handleKeyPress}
                                className={`w-full p-2 border rounded font-inter ${
                                    nameError ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-ps-blue-dark'
                                }`}
                                placeholder={t('dashboard.menuNamePlaceholder') || 'Bijv. Lunchkaart, Dinerkaart...'}
                                required
                                disabled={isSaving}
                                autoFocus
                                maxLength={100}
                            />
                            {nameError && (
                                <p className="text-red-500 text-sm mt-1 font-inter">
                                    {nameError}
                                </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1 font-inter">
                                {name.length}/100 {t('common.characters') || 'karakters'}
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-8">
                        <Button
                            onClick={handleClose}
                            variant="outline"
                            disabled={isSaving}
                            className="px-6 font-inter"
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={isSaving || !name.trim() || nameError}
                            className="bg-ps-green hover:bg-ps-green/90 text-white px-6 font-inter min-w-[100px]"
                        >
                            {isSaving ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    {t('common.saving')}
                                </>
                            ) : (
                                t('common.save')
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

MenuModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
};

export default MenuModal;